import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCSVStore, UploadStep } from '../stores/csvStore';
import { Location } from '../types/data/csv';
import { FileUploader } from '../components/csv/FileUploader';
import CSVPreview from '../components/csv/CSVPreview';
import ValidationResults from '../components/csv/ValidationResults';
import Sidebar from '../components/shared/Sidebar';
import useAuthStore from '../store/authStore';

// Map the enum steps to numeric indices for the UI
const UPLOAD = 0;
const PREVIEW = 1;
const VALIDATE = 2;
const CONFIRM = 3;

const CSVUploadPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { 
    uploadedFile, 
    validationResults, 
    clearData, 
    validateCurrentFile, 
    currentStep, 
    setCurrentStep,
    accessibleLocations,
    selectedLocation,
    setAccessibleLocations,
    isMultiLocationMode
  } = useCSVStore();
  const [activeStep, setActiveStep] = useState<number>(UPLOAD);
  const [loading, setLoading] = useState(true);

  // Fetch user's accessible locations on component mount
  useEffect(() => {
    fetchAccessibleLocations();
  }, []);

  // Trigger validation when entering the validation step
  useEffect(() => {
    if (activeStep === VALIDATE && uploadedFile && !validationResults) {
      validateCurrentFile();
    }
  }, [activeStep, uploadedFile, validationResults, validateCurrentFile]);

  const fetchAccessibleLocations = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/accessible-locations`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch accessible locations: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setAccessibleLocations(data.data);
      }
    } catch (error) {
      console.error('Error fetching accessible locations:', error);
      setAccessibleLocations([]);
    } finally {
      setLoading(false);
    }
  };

  const goToDashboard = () => {
    navigate('/dashboard');
  };

  const goToNextStep = () => {
    if (activeStep < CONFIRM) {
      setActiveStep((prevStep) => prevStep + 1);
    }
  };

  const goToPreviousStep = () => {
    if (activeStep > UPLOAD) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const goToMapData = () => {
    navigate('/map-data');
  };

  const handleStartOver = () => {
    clearData();
    setActiveStep(UPLOAD);
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case UPLOAD:
        return (
          <div className="upload-step">
            <h2>
              {loading 
                ? 'Upload CSV File' 
                : isMultiLocationMode
                  ? `Upload CSV File (Multiple Locations)`
                  : accessibleLocations.length === 1 
                    ? `Upload CSV File for ${accessibleLocations[0].name}`
                    : selectedLocation
                      ? `Upload CSV File for ${selectedLocation.name}`
                      : accessibleLocations.length > 1
                        ? `Upload CSV File (${accessibleLocations.length} locations accessible)`
                        : 'Upload CSV File'
              }
            </h2>
            <p>
              Upload your financial data CSV file for validation. We'll help you ensure your data 
              meets the required format and contains all necessary information.
            </p>
            <FileUploader onFileProcessed={goToNextStep} />
          </div>
        );
      case PREVIEW:
        return (
          <div className="upload-step preview-step">
            <div className="step-header">
              <h2>Preview Data</h2>
              <button className="back-button" onClick={goToPreviousStep}>
                Back
              </button>
            </div>
            <p>
              Review your uploaded data before proceeding with validation. Make sure the data appears 
              correct and properly formatted.
            </p>
            {uploadedFile ? (
              <>
                <div className="preview-layout">
                  {/* Format Detection Panel */}
                  {uploadedFile.detectedFormat && (
                    <div className="format-detection-panel">
                      <h4 className="format-panel-title">🔍 Detected Format</h4>
                      <div className="format-info-grid">
                        <div className="format-info-item">
                          <span className="format-info-label">Delimiter:</span>
                          <span className="format-info-value">
                            {uploadedFile.detectedFormat.delimiter === ',' && '🔸 Comma (,)'}
                            {uploadedFile.detectedFormat.delimiter === ';' && '🔸 Semicolon (;)'}
                            {uploadedFile.detectedFormat.delimiter === '\t' && '🔸 Tab'}
                            {uploadedFile.detectedFormat.delimiter === '|' && '🔸 Pipe (|)'}
                            {![',', ';', '\t', '|'].includes(uploadedFile.detectedFormat.delimiter) && `🔸 ${uploadedFile.detectedFormat.delimiter}`}
                          </span>
                        </div>
                        
                        <div className="format-info-item">
                          <span className="format-info-label">Format:</span>
                          <span className="format-info-value">
                            {uploadedFile.detectedFormat.isEuropeanFormat ? 
                              '🌍 European' : 
                              '🇺🇸 Standard'}
                          </span>
                        </div>
                        
                        <div className="format-info-item">
                          <span className="format-info-label">Numbers:</span>
                          <span className="format-info-value">
                            {uploadedFile.detectedFormat.isEuropeanFormat ? 
                              '1.234,56' : 
                              '1,234.56'}
                          </span>
                        </div>
                        
                        {uploadedFile.detectedFormat.quoteChar && (
                          <div className="format-info-item">
                            <span className="format-info-label">Quotes:</span>
                            <span className="format-info-value">🔸 {uploadedFile.detectedFormat.quoteChar}</span>
                          </div>
                        )}
                        
                        {uploadedFile.detectedFormat.warnings && uploadedFile.detectedFormat.warnings.length > 0 && (
                          <div className="format-info-item format-warnings">
                            <span className="format-info-label">⚠️ Warnings:</span>
                            <div className="format-warning-list">
                              {uploadedFile.detectedFormat.warnings.map((warning, index) => (
                                <div key={index} className="format-warning-item">{warning}</div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* CSV Preview */}
                  <CSVPreview file={uploadedFile} />
                </div>
                <div className="validation-actions">
                  <button className="primary-button" onClick={goToMapData}>
                    Proceed to Map
                  </button>
                </div>
              </>
            ) : (
              <div className="empty-state">No file uploaded. Please go back and upload a file.</div>
            )}
          </div>
        );
      case VALIDATE:
        return (
          <div className="upload-step">
            <div className="step-header">
              <h2>Validation Results</h2>
              <button className="back-button" onClick={goToPreviousStep}>
                Back
              </button>
            </div>
            <p>
              We've analyzed your data against our validation rules. Review the results below.
            </p>
            {uploadedFile ? (
              <>
                <ValidationResults 
                  onConfirm={goToNextStep} 
                  onReupload={handleStartOver} 
                />
              </>
            ) : (
              <div className="empty-state">No file uploaded. Please go back and upload a file.</div>
            )}
          </div>
        );
      case CONFIRM:
        return (
          <div className="upload-step confirmation-step">
            <div className="confirmation-message">
              <div className="success-icon">✓</div>
              <h2>Upload Complete!</h2>
              <p>
                Your CSV file has been successfully validated and uploaded to the system.
                You can now proceed to the dashboard or upload another file.
              </p>
            </div>
            <div className="confirmation-actions">
              <button className="secondary-button" onClick={handleStartOver}>
                Upload Another File
              </button>
              <button className="primary-button" onClick={goToDashboard}>
                Return to Dashboard
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="breadcrumbs">
            <button className="breadcrumb-link" onClick={goToDashboard}>
              Dashboard
            </button>
            <span className="breadcrumb-separator">/</span>
            <span className="breadcrumb-current">CSV Upload</span>
          </div>

          <div className="csv-upload-container">
            <div className="stepper">
              <div className={`step ${activeStep === UPLOAD ? 'active' : ''} ${activeStep > UPLOAD ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > UPLOAD ? '✓' : '1'}</div>
                <div className="step-label">Upload</div>
              </div>
              <div className={`step ${activeStep === PREVIEW ? 'active' : ''} ${activeStep > PREVIEW ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > PREVIEW ? '✓' : '2'}</div>
                <div className="step-label">Preview</div>
              </div>
              <div className={`step ${activeStep === VALIDATE ? 'active' : ''} ${activeStep > VALIDATE ? 'completed' : ''}`}>
                <div className="step-number">{activeStep > VALIDATE ? '✓' : '3'}</div>
                <div className="step-label">Validate</div>
              </div>
              <div className={`step ${activeStep === CONFIRM ? 'active' : ''}`}>
                <div className="step-number">4</div>
                <div className="step-label">Confirm</div>
              </div>
            </div>

            {renderStepContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVUploadPage; 